import { useForm } from "@refinedev/antd";
import { useNotification } from "@refinedev/core";
import { Button, DatePicker, Form, Input, InputNumber, Row, Col, Tag } from "antd";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import dataProvider from "../../providers/data";
import { PageHeader } from "../../components/ui";
import { RollbackOutlined, SearchOutlined } from "@ant-design/icons";

const Search = (Input as any).Search;

interface SaleData {
    id: number;
    invoiceNo: string;
    customerTPIN: string;
    customerName: string;
    salesStatusCode: string;
    currencyTypeCode: string;
    remark: string;
    totalItemCount: number;
    totalTaxableAmount: number;
    totalTax: number;
    totalAmount: number;
    status: string;
    items: any[];
    payments: any[];
    taxes: any[];
}

export const SaleReturnCreate: React.FC = () => {
    const navigate = useNavigate();
    const { form, formProps, saveButtonProps } = useForm({
        resource: "salereturns",
        redirect: "list",
        onMutationSuccess: () => {
            navigate('/sale-returns');
        },
    });

    const [saleData, setSaleData] = useState<SaleData | null>(null);
    const [loading, setLoading] = useState(false);
    const [searchParams] = useSearchParams();
    const { open } = useNotification();

    const handleSearch = useCallback(async (value: string) => {
        if (!value) return;
        setLoading(true);

        try {
            const { data } = await dataProvider.getSaleInvoice({ invoiceNumber: value });
            setSaleData(data);

            form.setFieldsValue({
                customerTPIN: data.customerTPIN,
                customerName: data.customerName,
                currencyTypeCode: data.currencyTypeCode,
                returnAmount: data.totalAmount,
                saleId: data.id,
                exchangeRate: data.exchangeRate || 1
            });
        } catch (error: any) {
            console.error('Error fetching sale details:', error);
            open({
                type: 'error',
                message: 'Error',
                description: error.message || 'Failed to fetch sale details'
            });
            setSaleData(null);
        } finally {
            setLoading(false);
        }
    }, [form, open]);

    useEffect(() => {
        const invoiceNumber = searchParams.get('invoiceNumber');
        if (invoiceNumber) {
            form.setFieldValue('saleNumber', invoiceNumber);
            form.setFieldValue('returnDate', dayjs());
            handleSearch(invoiceNumber);
        }
    }, [searchParams, handleSearch, form]);

    const getStatusColor = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'completed':
                return 'green';
            case 'pending':
                return 'orange';
            case 'cancelled':
                return 'red';
            default:
                return 'blue';
        }
    };

    return (
        <div className="page-container">
            <PageHeader
                title="Create Sale Return"
                icon={<RollbackOutlined />}
                breadcrumbs={[
                    { title: "Sale Returns", href: "/sale-returns" },
                    { title: "Create" },
                ]}
            />

            <Form {...formProps} layout="vertical" className="create-form-layout">
                {/* Search Section */}
                <div className="info-card" style={{ marginBottom: 16 }}>
                    <div className="info-card-header">
                        <span className="info-card-title">Find Sale Invoice</span>
                    </div>
                    <div className="info-card-body">
                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label="Sale Number"
                                    name="saleNumber"
                                    rules={[{ required: true, message: 'Please enter a sale number' }]}
                                >
                                    <Search
                                        placeholder="Enter invoice number and press Enter"
                                        enterButton={<><SearchOutlined /> Search</>}
                                        loading={loading}
                                        onSearch={handleSearch}
                                        size="large"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>
                </div>

                {/* Sale Details */}
                {saleData && (
                    <div className="info-card" style={{ marginBottom: 16 }}>
                        <div className="info-card-header">
                            <span className="info-card-title">Original Sale Details</span>
                            <Tag color={getStatusColor(saleData.status)}>{saleData.status}</Tag>
                        </div>
                        <div className="info-card-body">
                            <div className="info-cards-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                                <div className="info-item">
                                    <span className="info-label">Invoice No:</span>
                                    <span className="info-value" style={{ fontWeight: 'bold' }}>{saleData.invoiceNo}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Customer:</span>
                                    <span className="info-value">{saleData.customerName}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">TPIN:</span>
                                    <span className="info-value">{saleData.customerTPIN || 'N/A'}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Currency:</span>
                                    <span className="info-value">{saleData.currencyTypeCode}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Total Amount:</span>
                                    <span className="info-value" style={{ color: 'var(--color-primary)', fontWeight: 'bold' }}>
                                        ${saleData.totalAmount?.toFixed(2)}
                                    </span>
                                </div>
                                <div className="info-item">
                                    <span className="info-label">Total Tax:</span>
                                    <span className="info-value">${saleData.totalTax?.toFixed(2)}</span>
                                </div>
                                {saleData.remark && (
                                    <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                                        <span className="info-label">Remark:</span>
                                        <span className="info-value">{saleData.remark}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Return Information */}
                <div className="info-card" style={{ marginBottom: 16 }}>
                    <div className="info-card-header">
                        <span className="info-card-title">Return Information</span>
                    </div>
                    <div className="info-card-body">
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} md={6}>
                                <Form.Item
                                    label="Customer TPIN"
                                    name="customerTPIN"
                                    rules={[{ required: true }]}
                                >
                                    <Input disabled placeholder="Auto-filled from sale" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Form.Item
                                    label="Customer Name"
                                    name="customerName"
                                    rules={[{ required: true }]}
                                >
                                    <Input disabled placeholder="Auto-filled from sale" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Form.Item
                                    label="Currency"
                                    name="currencyTypeCode"
                                    rules={[{ required: true }]}
                                >
                                    <Input disabled placeholder="Auto-filled from sale" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Form.Item
                                    label="Exchange Rate"
                                    name="exchangeRate"
                                    rules={[{ required: true }]}
                                    initialValue={1}
                                >
                                    <InputNumber
                                        style={{ width: "100%" }}
                                        precision={4}
                                        min={0}
                                        max={999999999}
                                        disabled
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label="Return Date"
                                    name="returnDate"
                                    rules={[{ required: true, message: 'Please select a return date' }]}
                                    initialValue={dayjs()}
                                >
                                    <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    label="Return Amount"
                                    name="returnAmount"
                                    rules={[{ required: true }]}
                                >
                                    <InputNumber
                                        style={{ width: "100%" }}
                                        precision={2}
                                        min={0}
                                        max={999999999}
                                        disabled
                                        placeholder="Auto-calculated from sale"
                                        formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                        parser={(value: string) => value ? Number(value.replace(/\$\s?|(,*)/g, '')) : 0}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <Form.Item
                                    label="Return Reason"
                                    name="returnReason"
                                    rules={[{ required: true, message: 'Please provide a reason for the return' }]}
                                >
                                    <Input.TextArea
                                        placeholder="Explain why this sale is being returned..."
                                        rows={4}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>
                </div>

                {/* Hidden Fields */}
                <Form.Item name="saleId" hidden>
                    <Input />
                </Form.Item>

                {/* Submit Button */}
                <Button
                    {...saveButtonProps}
                    type="primary"
                    size="large"
                    style={{ width: '100%', marginTop: 24 }}
                    disabled={!saleData}
                >
                    Create Sale Return
                </Button>
            </Form>
        </div>
    );
};
